/**
 * @file customerPricing.js
 * @version 1.7.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-09-13
 * @modified 2026-10-28
 * @description Which price applies to this customer, for this item, on the
 *              DELIVERY DATE — and nothing wider than that.
 *
 *              The order screen loads contracts, spot prices and milk powder
 *              contracts scoped to every CANDIDATE date (the delivery date plus
 *              each option date) and, for contracts, to the period dropdown as
 *              well. That is right for the fetch: an option date's price should
 *              be offerable. It is wrong for deciding what applies, and the
 *              contract/spot icons were reading that wider set directly — so an
 *              item contracted in a different period, or on an option date we
 *              did not end up choosing, lit the handshake as though it were
 *              contracted for this order.
 *
 *              Everything here narrows to the delivery date alone. Dates are
 *              compared as YYYY-MM-DD strings: period boundaries are plain
 *              dates, and a Date-vs-Date comparison mixes local midnight with
 *              Postgres's UTC midnight, which under BST puts a delivery date
 *              exactly on a period's start date one hour OUTSIDE its own period.
 */

import { toISODate } from './periodHelper';
import { getTaxRate } from './taxTypes';
import { rangeOf as mpRangeOf, coversDay as mpCoversDay } from './milkPowderDates';
import { itemSalesTextOf, itemPurchaseTextOf } from './pickedItemWords';

const sameItem = (row, itemId, productCode) =>
  (itemId != null && (row.inventory_item_id === itemId || row.product_id === itemId))
  || (!!productCode && !!row.product_code && row.product_code === productCode);

/**
 * Does this contract row apply on this delivery date?
 *
 * Three shapes of row arrive from /customer-contracts, most specific first:
 *
 *   1. A STRAIGHTS row is one calendar month of monthly_pricing. It applies to
 *      that month only. A customer's Winter period holds a row per month and
 *      they genuinely differ — Ground Maize ran £236 to £270 across fifteen of
 *      them — so the month has to be matched, not the period.
 *   2. A row with its OWN start/end dates applies between them. A customer
 *      routinely holds two Winter 2025-26 contracts for one product, Oct-Dec at
 *      one price and Jan-Apr at another. The dates are what separates them.
 *   3. Only a row with neither falls back to the period name.
 *
 * The first two are EXCLUSIVE: a row whose own window does not cover the day
 * does not apply, whatever its period is called. That is the whole point of
 * carrying the window.
 */
export function contractAppliesOn(row, { deliveryDate, periodName }) {
  if (!row) return false;
  const day = toISODate(deliveryDate);

  if (row.month != null && row.year != null) {
    if (!day) return false;
    const [y, m] = day.split('-');
    return Number(y) === Number(row.year) && Number(m) === Number(row.month);
  }

  const start = toISODate(row.start_date);
  const end = toISODate(row.end_date);
  if (start || end) {
    if (!day) return false;
    return (!start || start <= day) && (!end || end >= day);
  }

  return !!periodName && row.period_name === periodName;
}

/**
 * Order of preference where more than one contract still applies. Eleven
 * customers hold two live contracts for one product over the SAME window at
 * different prices — account 467's Beef blend is £281 on 210T and £285 on 0T,
 * both created in the same second — so a tie is real and has to break the same
 * way every time rather than on whichever row the query happened to return.
 *
 *   1. A live contract beats one on hold. A held contract is one the office has
 *      stopped ordering against.
 *   2. A contract with tonnage left beats one already used up.
 *   3. A contract carrying a tonnage at all beats one carrying none.
 *
 * Lower is better.
 */
function contractRank(c) {
  if (c.is_on_hold) return 3;
  const contracted = Number(c.contracted_quantity) || 0;
  const ordered = Number(c.ordered_quantity) || 0;
  if (contracted > 0 && ordered < contracted) return 0;
  if (contracted > 0) return 1;
  return 2;
}

/**
 * Identifies a contract row across both tables it can come from. A straights
 * month and a blend contract can share an id — they are different tables.
 */
export const contractKeyOf = (c) => (c ? `${c.month != null ? 'monthly' : 'contract'}:${c.id}` : null);

/**
 * An additive recipe as a comparable string: the same additives in any order
 * read the same. By id where there is one, else code, else name — a free-text
 * additive has neither id nor code.
 */
export function additiveSignature(additives) {
  if (!Array.isArray(additives) || additives.length === 0) return '';
  return additives
    .map(a => {
      const id = a?.additive_id ?? a?.id;
      if (id != null) return `id:${id}`;
      const code = a?.additive_code ?? a?.code;
      if (code) return `code:${String(code).toLowerCase()}`;
      return `name:${String(a?.additive_name ?? a?.name ?? '').toLowerCase()}`;
    })
    .sort()
    .join('|');
}

/**
 * The customer's contract for this item, valid on the delivery date.
 * `periodName` is the delivery date's period — pass what periodNameForDate()
 * returns for the delivery date, NOT the period dropdown's value.
 *
 * A customer can hold the same feed twice in one window, plain and with an
 * additive, at different prices — Waddon Farms' Supreme Dairy is £310 plain
 * and £342 with C16 2%. So, most specific first:
 *
 *   - `contractKey`: the very contract picked from the contracted list.
 *   - `additives`: the contract whose additives are exactly the line's. A line
 *     with none gets the plain contract. Pass nothing to skip this step.
 *   - otherwise the ranking below, as before.
 */
export function contractForDeliveryDate(contracts, { itemId, productCode, periodName, deliveryDate, additives, contractKey }) {
  if (!Array.isArray(contracts)) return null;
  const applies = contracts.filter(c =>
    sameItem(c, itemId, productCode)
    && !c.is_archived
    && contractAppliesOn(c, { deliveryDate, periodName })
  );
  if (applies.length === 0) return null;
  const best = (list) => list.reduce((b, c) => (contractRank(c) < contractRank(b) ? c : b));

  if (contractKey) {
    const picked = applies.find(c => contractKeyOf(c) === contractKey);
    if (picked) return picked;
  }
  if (Array.isArray(additives)) {
    const want = additiveSignature(additives);
    const same = applies.filter(c => additiveSignature(c.additives) === want);
    if (same.length) return best(same);
  }
  return best(applies);
}

/**
 * The customer's spot price covering the delivery date. Spot rows carry their
 * own start/end dates, so no period is involved.
 */
export function spotForDeliveryDate(spotPrices, { itemId, productCode, deliveryDate }) {
  const day = toISODate(deliveryDate);
  if (!Array.isArray(spotPrices) || !day) return null;
  return spotPrices.find(sp => {
    if (!sameItem(sp, itemId, productCode)) return false;
    const start = toISODate(sp.start_date);
    const end = toISODate(sp.end_date);
    return (!start || start <= day) && (!end || end >= day);
  }) || null;
}

/**
 * The customer's milk powder contract covering the delivery date. Each runs
 * From valid_from To valid_to (inclusive); a row carrying only price_month
 * (a server without the dates yet) covers that whole month — utils/milkPowderDates,
 * the same rule the server uses. Not tied to a contract period.
 */
export function milkPowderForDeliveryDate(mpContracts, { itemId, productCode, deliveryDate }) {
  const day = toISODate(deliveryDate);
  if (!Array.isArray(mpContracts) || !day) return null;
  return mpContracts.find(m =>
    sameItem(m, itemId, productCode)
    && (!mpRangeOf(m) || mpCoversDay(m, day))
  ) || null;
}

/**
 * Everything the item list needs to decorate one row, all narrowed to the
 * delivery date. `contract` covers both a regular customer contract and a milk
 * powder one, because to the person reading the list they mean the same thing.
 */
export function pricingBadgesFor({
  itemId,
  productCode,
  productName,
  contracts,
  spotPrices,
  milkPowderContracts,
  deliveryDate,
  periodName,
}) {
  const spotMatch = spotForDeliveryDate(spotPrices, { itemId, productCode, deliveryDate });
  const mpContract = milkPowderForDeliveryDate(milkPowderContracts, { itemId, productCode, deliveryDate });
  const contract = contractForDeliveryDate(contracts, { itemId, productCode, periodName, deliveryDate });
  return {
    spotMatch,
    mpContract,
    contract,
    hasContract: !!contract || !!mpContract,
    hasSpot: !!spotMatch,
    isDeccox: (productName || '').toLowerCase().includes('deccox'),
  };
}

/**
 * An inventory item's code and name, whichever shape it arrived in.
 *
 * Both are real and both occur. /api/orders/inventory-items aliases the columns
 * (`item_code as product_code`), so the order screen's list carries
 * product_code / product_name; /api/inventory returns the table's own
 * item_code / item_name, which is what a newly created item comes back as.
 * Reading only one of the pair is what put "undefined undefined" in the Item
 * cell, so the fallback belongs here rather than at each call site.
 */
export const itemCodeOf = (i) => i?.item_code ?? i?.product_code ?? '';
export const itemNameOf = (i) => i?.item_name ?? i?.product_name ?? '';

/** How an item reads in a single line of text. */
export const itemLabelOf = (i) => [itemCodeOf(i), itemNameOf(i)].filter(Boolean).join(' ');

/**
 * The two wordings an order line starts from (Cam, 2026-10-02):
 *   Sale     — the item's Sales Description, else its name. Goes on the invoice.
 *   Purchase — the item's Purchase Description, else its name. Goes on the PO
 *              and the supplier's bill. NEVER the sales wording: on blends that
 *              names the customer, and the mill must not see our customer.
 * One rule, with which words a picked item gives each document's line, in
 * utils/pickedItemWords (2026-10-28); re-exported here for existing callers.
 */
export { itemSalesTextOf, itemPurchaseTextOf };

const num = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : null;
};

/**
 * Everything a grid row should become once an inventory item is chosen.
 *
 * This replaces six near-identical builders that each produced a different
 * subset of the same fields — one per tab picker, plus reorder and standing
 * order — and so disagreed about which ones a line carried. The order below is
 * most-specific-first, and it is the whole of the pricing policy:
 *
 *   1. a customer contract covering the delivery date's period
 *   2. straights monthly pricing (a contract row in a straights category)
 *   3. a milk powder contract for the delivery date's month
 *   4. a spot price covering the delivery date
 *   5. the item's own sale price
 *
 * `cost` is advisory: it feeds the Unit Cost Price cell as a suggestion, and is
 * never what gets billed. An additive's cost is NOT added here — additives are
 * attached per line and their cost is summed separately, so that a line's cost
 * can say which part is the item and which the recipe.
 */
export function resolveLineForItem({
  item,
  contracts = [],
  spotPrices = [],
  milkPowderContracts = [],
  suppliers = [],
  deliveryDate = null,
  periodName = null,
  // The line's additives, so the matching contract version is chosen, and the
  // contract picked from the list when there was one. See contractForDeliveryDate.
  additives = undefined,
  contractKey = null,
}) {
  if (!item) return null;

  const itemId = item.id ?? item.inventory_item_id;
  const productCode = itemCodeOf(item);
  const productName = itemNameOf(item);
  const lookup = { itemId, productCode, deliveryDate, periodName };

  const contract = contractForDeliveryDate(contracts, { ...lookup, additives, contractKey });
  const mp = milkPowderForDeliveryDate(milkPowderContracts, lookup);
  const spot = spotForDeliveryDate(spotPrices, lookup);

  let source = 'inventory';
  let unitPrice = num(item.unit_price ?? item.sales_unit_price) ?? 0;
  let cost = num(item.purchase_unit_price);
  let contractId = null;
  let monthlyPricingId = null;
  // The milk powder price it was priced from (28 Oct 2026): the order records it.
  let milkPowderPriceId = null;
  let categoryName = null;
  let taxType = item.tax_type ?? item.sales_tax_type ?? null;
  let extra = {};

  if (contract) {
    const isStraights = contract.category_name === 'straights';
    source = 'contract';
    unitPrice = num(contract.price) ?? unitPrice;
    cost = num(contract.cost) ?? cost;
    taxType = contract.tax_type ?? taxType;
    categoryName = contract.category_name ?? null;
    if (isStraights) monthlyPricingId = contract.id;
    else contractId = contract.id;
    extra = {
      contracted_quantity: contract.contracted_quantity ?? null,
      ordered_quantity: contract.ordered_quantity ?? null,
      month: contract.month ?? null,
      year: contract.year ?? null,
      // Shown on the line and quoted back on the purchase order. Both are
      // usually blank — 4 of 1090 contracts carry ours — so everything that
      // renders them has to cope with nothing.
      contract_number: contract.contract_number || null,
      supplier_contract_number: contract.supplier_contract_number || null,
    };
  } else if (mp) {
    source = 'milk_powder';
    milkPowderPriceId = mp.id ?? null;
    unitPrice = num(mp.price) ?? unitPrice;
    cost = num(mp.cost) ?? cost;
    taxType = mp.tax_type ?? taxType;
  } else if (spot) {
    source = 'spot';
    unitPrice = num(spot.price) ?? unitPrice;
    taxType = spot.tax_type ?? taxType;
    // Spot prices live in customer_contracts, so the line points at one — that
    // is what makes it a spot line rather than a bare inventory one.
    contractId = spot.id;
  }

  /*
   * The supplier is never filled in — not guessed from the item code, and
   * (since 2026-09-29, Cam's call) not taken from the contract either. It is
   * chosen on every line, by the person raising the order: the save refuses a
   * line without one. The contract still decides the price, and the PO still
   * quotes the contract's numbers (worked out on the server).
   */
  return {
    inventory_item_id: itemId ?? null,
    product_name: productName,
    product_code: productCode,
    // The item's two wordings, for a customer ORDER line to start from (the
    // grid's order variant applies them; an invoice keeps product_name).
    sales_text: itemSalesTextOf(item),
    purchase_text: itemPurchaseTextOf(item),
    unit_price: unitPrice,
    cost_price: cost,
    tax_type: taxType,
    tax_rate: getTaxRate(taxType) || 0,
    contract_id: contractId,
    monthly_pricing_id: monthlyPricingId,
    milk_powder_price_id: milkPowderPriceId,
    category_name: categoryName,
    source,
    supplier_contact_id: null,
    supplier_name: null,
    supplier_account_no: null,
    // A contract's additives are its recipe — carry them onto the line so the
    // purchase order tells the supplier what to mill.
    additives: Array.isArray(contract?.additives) && contract.additives.length
      ? contract.additives.map(a => ({
          additive_id: a.id ?? a.additive_id,
          additive_code: a.code ?? a.additive_code,
          additive_name: a.name ?? a.additive_name,
          additive_price: a.price ?? a.additive_price ?? null,
        }))
      : [],
    ...extra,
  };
}

/**
 * The additive cost to add on top of an item's own cost, and whether any of it
 * is unknown. The flag matters: an unpriced additive contributes nothing to the
 * sum, so without saying so the total silently understates the real cost and
 * gets margin added to it as though it were complete.
 */
export function additiveCostFor(additives) {
  if (!Array.isArray(additives) || additives.length === 0) {
    return { total: 0, hasUnknown: false };
  }
  let total = 0;
  let hasUnknown = false;
  for (const a of additives) {
    const p = num(a.additive_price ?? a.price);
    if (p === null) hasUnknown = true;
    else total += p;
  }
  return { total, hasUnknown };
}
